import React from 'react';

import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import { outcome_chip } from './adapters';

/*
 * bank response lines (the preview and the held queue): what the bank said, which payment it
 * matched, and what applying does or did.
 */
// Four tracks (list-row pattern): bank line · matched payment · amount · outcome and action.
const columns = (on_open_teacher, action) => {
  const outcome_meta = (r) => [
    r.applied.label,
    r.ops_action ? `Ops: ${r.ops_action}${r.action_note ? ` (${r.action_note})` : ''}` : null
  ];
  const chip = (r) => {
    const o = outcome_chip(r.proposed);
    return <StatusPill tone={o.tone}>{o.label}</StatusPill>;
  };
  return [
    {
      key: 'bank',
      role: 'identifier',
      label: 'Bank says',
      track: { compact: '30%', full: '28%' },
      render: (r) => (
        <Who
          name={r.beneficiary_name}
          meta={[
            <>
              Line {r.line_no} · Bank status <span className="tp-mono">{r.status_code}</span>
            </>,
            [r.account_last4 ? `A/c …${r.account_last4}` : '', r.ifsc, r.value_date]
              .filter((x) => x && x !== '—')
              .join(' · '),
            r.bank_reference ? <span className="tp-mono">UTR {r.bank_reference}</span> : null,
            r.bank_reason || null
          ]}
        />
      )
    },
    {
      key: 'match',
      label: 'Matched',
      render: (r) => (
        <What
          title={<StatusPill tone={r.match.tone}>{r.match.label}</StatusPill>}
          lines={[
            r.teacher_uuid && on_open_teacher ? (
              <button
                type="button"
                className="tp-link"
                aria-label={`Open ${r.teacher_name || 'the teacher'}'s ${r.pay_month} payouts`}
                onClick={() => on_open_teacher(r.teacher_uuid, r.pay_month)}
              >
                {r.teacher_name || 'Teacher'} · {r.pay_month}
              </button>
            ) : null,
            r.item_state ? `Payment: ${r.item_state.label}` : null
          ]}
        />
      )
    },
    {
      key: 'amount',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      track: { compact: '140px', full: '148px' },
      render: (r) => <span className="tp-c-amt">{r.amount}</span>,
      card_render: (r) => <Ledger items={[{ label: 'Amount', value: r.amount }]} />
    },
    {
      key: 'outcome',
      role: 'status',
      label: 'Outcome',
      track: { compact: '188px', full: '220px' },
      render: (r) => (
        <StatusCell
          chip={chip(r)}
          meta={outcome_meta(r)}
          actions={
            action ? <span className="tp-c-acts tp-actions">{action(r, 'tp-link')}</span> : null
          }
        />
      ),
      card_render: (r) => <StatusCell chip={chip(r)} meta={outcome_meta(r)} />
    },
    {
      key: 'why_held',
      label: 'Why held',
      line: { from: 'match', bare: true },
      render: (r) => {
        const { reason } = outcome_chip(r.proposed);
        return reason ? <Note label="Why held" text={reason} warn /> : null;
      }
    },
    ...(action
      ? [
          {
            key: 'action',
            label: 'Action',
            role: 'actions',
            in_table: false,
            card_render: (r) => action(r, 'tp-btn-out')
          }
        ]
      : [])
  ];
};

const LinesTable = ({ rows, on_open_teacher, action, empty_text = 'No lines.' }) => (
  <DataTable
    columns={columns(on_open_teacher, action)}
    rows={rows}
    rowKey={(r) => String(r.line_id)}
    emptyText={empty_text}
    card_below={660}
  />
);

export default LinesTable;
